import {Button, IconButton, Link, Stack} from "@mui/material";
import {useState, forwardRef, useImperativeHandle} from "react";
import {useNavigate} from "react-router-dom";
import {FiTrash2} from "react-icons/fi";
import {tenantUserRolesColumns} from "../../constants/dataGridColumns";
import ListView from "../ListView/ListView";
import useApiRequest from "../../hooks/useApiRequest";
import useConfirmDialog from "../../hooks/useConfirmDialog";
import ConfirmDialog from "../ui/ConfirmDialog";
import {snackbar} from "../../utils/snackbar";
import TenantUserRolesFilterBar from "./TenantUserRolesFilterBar";
import TenantUserRolesAddForm from "./TenantUserRolesAddForm";

const normalizeArray = (value) => {
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.results)) return value.results;
  if (Array.isArray(value)) return value;
  return [];
};

const TenantUserRolesGrid = forwardRef(function TenantUserRolesGrid(
  {tenantId, productId, userId, roleId, canAssign = !!tenantId, canDelete = !!tenantId, onUsuariosLoaded},
  ref
) {
  const navigate = useNavigate();
  const [isAddingUserRole, setIsAddingUserRole] = useState(false);
  const [tenantUserRoles, setTenantUserRoles] = useState([]);

  const {makeRequest} = useApiRequest({baseUrl: `/tenants/${tenantId}/users`, resource: "", field: ""});
  const {loading, list: listTenantUserRoles} = useApiRequest({baseUrl: "/tenant-user-roles"});

  const {confirmOpen, itemName, openDialog, handleConfirm, handleCancel} = useConfirmDialog(async (rowId) => {
    const found = tenantUserRoles.find((r) => `${r.user_id}-${r.role_id}` === rowId);
    if (!found) return;

    const {success, error} = await makeRequest("delete", `/${found.user_id}/roles/${encodeURIComponent(found.role_id)}`, null, {silentError: true});
    if (!success) {
      if (error?.code === "FORBIDDEN") {
        snackbar.error("No tienes permisos suficientes para eliminar este usuario/rol del tenant.");
      } else {
        snackbar.error(error?.userMessage);
      }
      return;
    }

    snackbar.success("Rol del usuario eliminado del tenant correctamente.");
    setTenantUserRoles((prev) => prev.filter((r) => `${r.user_id}-${r.role_id}` !== rowId));
  });

  const handleSearch = async (params) => {
    const result = await listTenantUserRoles(params);
    if (result?.success) {
      let data = normalizeArray(result.data);
      setTenantUserRoles(data);
      if (typeof onUsuariosLoaded === "function") {
        onUsuariosLoaded(data);
      }
      return data;
    }
    return [];
  };

  useImperativeHandle(ref, () => ({
    reloadUsuarios: async () => {
      const params = {};
      if (tenantId) params.tenant_id = tenantId;
      return await handleSearch(params);
    }
  }));

  const handleAccept = async (selectedUserId, selectedRoleId) => {
    const {success, error} = await makeRequest("post", `/${selectedUserId}/roles`, {role_id: selectedRoleId}, {silentError: true});
    if (!success) {
      snackbar.error(error?.userMessage);
      return;
    }

    snackbar.success("Rol asignado al usuario correctamente.");
    const params = {};
    if (tenantId) params.tenant_id = tenantId;
    await handleSearch(params);
    setIsAddingUserRole(false);
  };

  const hiddenFields = [];
  if (tenantId) hiddenFields.push("tenant_name");
  if (productId) hiddenFields.push("product_name");

  const columns = [
    ...tenantUserRolesColumns
      .filter((col) => !hiddenFields.includes(col.field))
      .map((col) => {
        if (col.field === "tenant_name")
          return {
            ...col,
            renderCell: (params) => (
              <Link
                component="button"
                underline="hover"
                onClick={() => navigate("/tenant-form", {state: {id: params.row.tenant_id}})}
              >
                {params.value}
              </Link>
            )
          };
        if (col.field === "user_email")
          return {
            ...col,
            renderCell: (params) => (
              <Link
                component="button"
                underline="hover"
                onClick={() => navigate("/user-form", {state: {id: params.row.user_id}})}
              >
                {params.row.user_email || params.value}
              </Link>
            )
          };
        if (col.field === "role_name")
          return {
            ...col,
            renderCell: (params) => (
              <Link
                component="button"
                underline="hover"
                onClick={() => navigate("/role-form", {state: {id: params.row.role_id}})}
              >
                {params.value}
              </Link>
            )
          };
        if (col.field === "product_name")
          return {
            ...col,
            renderCell: (params) => (
              <Link
                component="button"
                underline="hover"
                onClick={() => navigate("/product-form", {state: {id: params.row.product_id}})}
              >
                {params.value}
              </Link>
            )
          };
        return col;
      }),
    ...(canDelete
      ? [
          {
            field: "actions",
            headerName: "Acciones",
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
              <IconButton
                aria-label="delete"
                color="error"
                onClick={(e) => {
                  e.stopPropagation();
                  const found = tenantUserRoles.find((r) => `${r.user_id}-${r.role_id}` === params.id);
                  openDialog(
                    params.id,
                    found ? `${found.user_email || found.user_id} - ${found.role_name || found.role_id}` : ""
                  );
                }}
              >
                <FiTrash2 />
              </IconButton>
            )
          }
        ]
      : [])
  ];

  return (
    <>
      <TenantUserRolesFilterBar
        tenantId={tenantId}
        productId={productId}
        userId={userId}
        roleId={roleId}
        onSearch={handleSearch}
      />
      <ListView
        columns={columns}
        data={tenantUserRoles}
        getRowId={(row) => `${row.user_id}-${row.role_id}`}
        loading={loading}
        topRightButtons={
          canAssign ? (
            <Stack direction="row" spacing={2} alignItems="center">
              {isAddingUserRole && (
                <TenantUserRolesAddForm
                  onAccept={handleAccept}
                  onCancel={() => setIsAddingUserRole(false)}
                  tenantId={tenantId}
                  productId={productId}
                />
              )}
              <Button
                variant="contained"
                color={isAddingUserRole ? "error" : "primary"}
                onClick={() => setIsAddingUserRole((prev) => !prev)}
              >
                {isAddingUserRole ? "Cancelar" : "Asignar usuario"}
              </Button>
            </Stack>
          ) : undefined
        }
      />
      <ConfirmDialog
        open={confirmOpen}
        onClose={handleCancel}
        onConfirm={handleConfirm}
        entityName="relación usuario-rol"
        entityLabel={itemName}
      />
    </>
  );
});

export default TenantUserRolesGrid;
